import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Pagination from "../../components/Pagination/Pagination";
import RecordsSummaryEditor from "../../components/RecordsSummaryEditor/RecordsSummaryEditor";
import ScoreFilterBar from "../../components/ScoreFilterBar/ScoreFilterBar";
import EditableScoreItem from "../../components/ScoreItems/EditableScoreItem";
import styles from "../../components/ScoreItems/ScoreItem.module.css";
import { useApi } from "../../hooks/useApi";
import { usePagination } from "../../hooks/usePagination";
import { useScoreFilters } from "../../hooks/useScoreFilters";

const MemberScores = () => {
    const { userId } = useParams();
    const { makeApiCall } = useApi();

    const [ user, setUser ] = useState(null);
    const [ scores, setScores] = useState([]);
    const [ summary, setSummary ] = useState(null);

    const [ isLoading, setIsLoading ] = useState(true);
    const [ isPendingAction, setIsPendingAction ] = useState(false);
    const [ error, setError ] = useState(null);

    const filterBarProps = useScoreFilters(scores);
    const { filters, displayedScores } = filterBarProps;

    const paginationProps = usePagination();
    const {
        loadNumber,
        currentPage, setCurrentPage,
        setTotalPages,
        totalCount, setTotalCount
    } = paginationProps;

    useEffect(() => {
        setCurrentPage(1);
    }, [filters, setCurrentPage]);

    const fetchScores = useCallback(async (page) => {
        setIsLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams({
                page,
                limit: loadNumber,
                ...filters
            }).toString();

            const response = await makeApiCall(`/api/scores/user/${userId}?${queryParams}`);
            if (!response) {
                return; // 401
            };

            if (response.ok) {
                const data = await response.json();

                setUser(data.user);
                setScores(data.scores);
                setSummary(data.summary);

                setTotalPages(data.pagination.totalPages);
                setTotalCount(data.pagination.totalCount);
            }

        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [ userId, makeApiCall, filters, loadNumber, setTotalPages, setTotalCount ]);

    useEffect(() => {
        fetchScores(currentPage);
    }, [currentPage, loadNumber, fetchScores]);

    const handleStatusUpdate = async (id, newStatus) => {
        setIsPendingAction(true);
        setError(null);

        try {
            const response = await makeApiCall(`/api/scores/${id}/status`, {
                method: "PATCH",
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error);
            }

            setScores(prev =>
                prev.map(score => score.id === id ? { ...score, status: newStatus } : score)
            );

        } catch (error) {
            setError(error.message);
        } finally {
            setIsPendingAction(false);
        }
    };

    const handleDelete = async (id) => {
        setIsPendingAction(true);
        setError(null);

        try {
            const response = await makeApiCall(`/api/scores/${id}`, {
                method: "DELETE"
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error);
            }

            setScores(prev =>
                prev.filter(score => score.id !== id)
            );
        } catch (error) {
            setError(error.message);
        } finally {
            setIsPendingAction(false);
        }
    };

    return (
        <div className="content">
            <Breadcrumbs customLabel={`${user?.firstName} ${user?.lastName}`} />

            <div>
                <h2>Scores for {`${user?.firstName} ${user?.lastName}`}</h2>
                <RecordsSummaryEditor userId={userId} initialSummary={summary} />
                <ScoreFilterBar
                    filterBarProps={filterBarProps}
                    paginationProps={paginationProps}
                />

                <p className="small">{ totalCount } scores found. { scores.length !== totalCount && <span>({scores.length} displayed.)</span> }</p>
            </div>

            <div className={styles.scoreList}>
                { displayedScores.length > 0 ? (
                    displayedScores.map(score => (
                        <EditableScoreItem
                            key={score.id}
                            score={score}
                            onDelete={handleDelete}
                            onStatusUpdate={handleStatusUpdate}
                            isPending={isPendingAction}
                        />
                    ))
                ) : !isLoading && (
                    <p className="small centred">No scores to display.</p>
                )}
            </div>

            <Pagination paginationProps={paginationProps} />

            { error && <p className="error-message">{ error }</p>}
        </div>
    );
};

export default MemberScores;
